import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps } from "@lib/types/Api";
import { Provider, ProviderCustomerMatrix } from "@lib/types/Provider";
import { Divider, Form, Input, Select, Space, Switch, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ProviderFormItems from "../ProviderFormItems";
import ProviderLogo from "@lib/components/MiniComponents/Others/ProviderLogo";
import { useQuery } from "@tanstack/react-query";
import { apiGetCustomers } from "@lib/api/customer";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { createYupSync } from "@lib/helpers/TableHelper";
import { providerSchema } from "@lib/schemas/provider";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

type Props = {
  openModal: boolean;
  handleClose: () => void;
  mutationQuery: UseMutationProps<ProviderCustomerMatrix>;
  defaultProvider?: Provider;
  providerMatrix: ProviderCustomerMatrix;
};

export default function ProviderCustomerMatrixForm(props: Props) {
  const {
    openModal,
    handleClose,
    mutationQuery,
    defaultProvider,
    providerMatrix,
  } = props;
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const { mutateAsync, isPending } = mutationQuery;
  const [openAdvanceSettings, setOpenAdvanceSettings] = useState(false);

  const initialValues = useMemo(() => {
    const data = {
      enabled: true,
      ...providerMatrix,
    } as ProviderCustomerMatrix;

    return data;
  }, [providerMatrix]);

  useEffect(() => {
    form?.resetFields();
    setOpenAdvanceSettings(false);
  }, [openModal, handleClose]);

  useEffect(() => {
    if (typeof form?.setFieldsValue !== "undefined") {
      form?.resetFields();
      form?.setFieldsValue(initialValues);
    }
    setOpenAdvanceSettings(
      !!Object.keys(providerMatrix?.overrideConfig || {}).length,
    );
  }, [form, openModal, initialValues]);

  useEffect(() => {
    if (openAdvanceSettings) {
      if (!!providerMatrix) {
        form?.setFieldsValue({
          overrideConfig: providerMatrix?.overrideConfig,
        });
      } else {
        form?.setFieldsValue({ overrideConfig: defaultProvider?.config });
      }
    }
  }, [openAdvanceSettings]);

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: !!openModal,
  });

  return (
    <Form<ProviderCustomerMatrix>
      {...layout}
      form={form}
      name={"provider-matrix-modal"}
      onFinish={(values) => {
        if (!openAdvanceSettings) {
          values.overrideConfig = {};
        }
        if (Boolean(providerMatrix)) {
          values.customerId = providerMatrix?.customerId;
        }

        mutateAsync({
          ...values,
          providerId: defaultProvider?.id,
          id: initialValues?.id,
        }).then(() => {
          form?.resetFields();
          handleClose();
        });
      }}
      style={{ maxWidth: 800, marginTop: 20 }}
      initialValues={initialValues}
      autoComplete="off"
    >
      <StyledFlex style={{ gap: 8, marginBottom: 25 }}>
        <StyledFlex
          style={{
            flexDirection: "column",
          }}
        >
          <ProviderLogo provider={defaultProvider} />

          <Typography.Text>{defaultProvider?.name}</Typography.Text>
        </StyledFlex>
      </StyledFlex>

      {providerMatrix?.id && (
        <Form.Item
          name="id"
          label={messages["common.apiKey"] as string}
          rules={[createYupSync({ schema: providerSchema })]}
          required
        >
          <Input disabled />
        </Form.Item>
      )}

      <Form.Item
        name="customerId"
        label={messages["common.customer"] as string}
        rules={[createYupSync({ schema: providerSchema })]}
        required
      >
        <Select
          placeholder={messages["common.makeYourChoice"] as string}
          disabled={customersIsLoading || Boolean(providerMatrix)}
          loading={customersIsLoading}
          showSearch
          optionFilterProp="text"
          options={customers?.data?.data?.map((customer) => ({
            label: <CustomerName customer={customer} />,
            text: customer.name,
            value: customer.id,
          }))}
        />
      </Form.Item>

      <Form.Item
        name="enabled"
        label={messages["common.status"] as string}
        valuePropName="checked"
        rules={[createYupSync({ schema: providerSchema })]}
        required
      >
        <Switch
          checked={initialValues.enabled}
          checkedChildren={messages["common.enabled"] as string}
          unCheckedChildren={messages["common.disabled"] as string}
        />
      </Form.Item>

      <Divider>
        {messages["common.advanceSettings"] as string}{" "}
        <Switch
          checked={openAdvanceSettings}
          checkedChildren={messages["common.enabled"] as string}
          unCheckedChildren={messages["common.disabled"] as string}
          onChange={() => setOpenAdvanceSettings(!openAdvanceSettings)}
        />
      </Divider>

      {openAdvanceSettings && (
        <>
          <Form.Item name="enabled" label={messages["common.note"] as string}>
            <Typography.Text>
              <FormattedMessage id="common.activeAdvanceSettingsNote" />
            </Typography.Text>
          </Form.Item>

          <ProviderFormItems
            objKey="overrideConfig"
            provider={defaultProvider}
          />
        </>
      )}

      <StyledFlex style={{ justifyContent: "end" }}>
        <Space>
          {!providerMatrix?.id && (
            <ActionButton
              type="reset"
              onClick={handleClose}
              loading={isPending}
            />
          )}

          <ActionButton
            type={providerMatrix?.id ? "save" : "add"}
            loading={isPending}
          />
        </Space>
      </StyledFlex>
    </Form>
  );
}
